import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { BUSINESS } from '@/lib/site';
import { PhoneIcon, MailIcon, ArrowRight, ChevronDown, CloseIcon, FaxIcon, ClockIcon, StarIcon } from '@/components/icons';

export default function StickyContact() {
  const [openDesktop, setOpenDesktop] = useState(true);
  const loc = useLocation();
  // hide on admin
  if (loc.pathname.startsWith('/admin')) return null;

  return (
    <>
      {/* Desktop right rail */}
      <aside
        className="hidden lg:flex fixed right-4 bottom-6 z-40 max-w-[300px]"
        data-testid="sticky-contact-rail"
      >
        <AnimatePresence initial={false}>
          {openDesktop ? (
            <motion.div
              key="open"
              initial={{ x: 30, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 30, opacity: 0 }}
              transition={{ duration: 0.22 }}
              className="bg-[#060D9C] text-white rounded-2xl border border-white/10 shadow-2xl overflow-hidden w-[280px]"
            >
              <div className="hi-vis-stripe h-1" />
              <div className="px-4 py-3 flex items-center justify-between border-b border-white/10">
                <div className="label-eyebrow text-[#FC4E2C]">Contact NFT</div>
                <button
                  onClick={() => setOpenDesktop(false)}
                  className="p-1 hover:bg-white/10 rounded"
                  aria-label="Minimize contact widget"
                  data-testid="sticky-contact-minimize"
                >
                  <CloseIcon width={16} height={16} />
                </button>
              </div>
              <div className="p-4 space-y-3 text-sm">
                <div className="flex items-start gap-2">
                  <ClockIcon width={16} height={16} className="shrink-0 mt-0.5 text-[#FC4E2C]" />
                  <span className="text-white/85 text-xs">{BUSINESS.hours}</span>
                </div>
                <a href={BUSINESS.phoneHref} className="flex items-center gap-2 hover:text-[#FC4E2C] transition-colors" data-testid="sticky-phone">
                  <PhoneIcon width={16} height={16} className="text-[#FC4E2C]" />
                  <span className="font-bold">{BUSINESS.phone}</span>
                </a>
                <div className="flex items-center gap-2 text-white/70">
                  <FaxIcon width={16} height={16} className="text-[#FC4E2C]" />
                  <span className="text-xs">Fax {BUSINESS.fax}</span>
                </div>
                <a href={BUSINESS.emailHref} className="flex items-center gap-2 hover:text-[#FC4E2C] transition-colors break-all">
                  <MailIcon width={16} height={16} className="text-[#FC4E2C]" />
                  <span className="text-xs">{BUSINESS.email}</span>
                </a>
                <div className="pt-2 border-t border-white/10">
                  <div className="label-eyebrow text-white/60 mb-1">Urgent / After Hours</div>
                  <a href={BUSINESS.mobileHref} className="text-[#FC4E2C] font-bold text-sm">
                    Text {BUSINESS.mobile}
                  </a>
                </div>
                <Link to="/booking" className="btn-primary-orange w-full justify-center text-xs" data-testid="sticky-book-cta">
                  Book a Course <ArrowRight width={14} height={14} />
                </Link>
              </div>
            </motion.div>
          ) : (
            <motion.button
              key="closed"
              initial={{ x: 30, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 30, opacity: 0 }}
              onClick={() => setOpenDesktop(true)}
              className="bg-[#FC4E2C] text-white rounded-full shadow-2xl px-5 py-3 flex items-center gap-2 font-bold uppercase"
              style={{ fontFamily: 'Barlow Semi Condensed', letterSpacing: '0.08em' }}
              data-testid="sticky-contact-expand"
            >
              <PhoneIcon width={16} height={16} /> Contact
            </motion.button>
          )}
        </AnimatePresence>
      </aside>

      {/* Mobile bottom bar */}
      <div
        className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-[#060D9C] text-white border-t border-white/10"
        data-testid="mobile-contact-bar"
      >
        <div className="hi-vis-stripe h-1" />
        <div className="flex items-stretch">
          <a href={BUSINESS.phoneHref} className="flex-1 flex items-center justify-center gap-1.5 py-3 active:bg-white/10">
            <PhoneIcon width={16} height={16} className="text-[#FC4E2C]" />
            <span className="text-xs font-bold uppercase" style={{ fontFamily: 'Barlow Semi Condensed' }}>Call</span>
          </a>
          <div className="w-px bg-white/10" />
          <a href={BUSINESS.emailHref} className="flex-1 flex items-center justify-center gap-1.5 py-3 active:bg-white/10">
            <MailIcon width={16} height={16} className="text-[#FC4E2C]" />
            <span className="text-xs font-bold uppercase" style={{ fontFamily: 'Barlow Semi Condensed' }}>Email</span>
          </a>
          <div className="w-px bg-white/10" />
          <Link to="/booking" className="flex-1 flex items-center justify-center gap-1.5 py-3 bg-[#FC4E2C] text-white">
            <ArrowRight width={16} height={16} />
            <span className="text-xs font-extrabold uppercase" style={{ fontFamily: 'Barlow Semi Condensed' }}>Book</span>
          </Link>
        </div>
        <div className="flex items-center justify-center gap-1.5 py-1 text-[10px] text-white/70">
          <span className="inline-flex items-center gap-0.5 text-[#FC4E2C]">
            {[0,1,2,3,4].map(i => <StarIcon key={i} width={9} height={9} />)}
          </span>
          <span>{BUSINESS.rating} • {BUSINESS.reviewCount} Google Reviews</span>
        </div>
      </div>
      {/* Spacer for mobile bottom bar */}
      <div className="lg:hidden h-[78px]" aria-hidden="true" />
    </>
  );
}
